import {
  ApartmentOutlined,
  BankOutlined,
  ControlOutlined,
  DashboardOutlined,
  DeploymentUnitOutlined,
  DesktopOutlined,
  EnvironmentOutlined,
  FileSearchOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  MobileOutlined,
  PartitionOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { UserMenu } from '@hq/account';
import { hasPermission } from '@hq/permission';
import { truncateByWidth } from '@hq/utils';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Drawer, Layout, Menu, Space, Tag, Typography, type MenuProps } from 'antd';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import MobileQrEntry from '../components/MobileQrEntry';
import { useIsMobile } from '../components/useMobile';
import { PLATFORM_ONLY_KEYS } from '../router/routePerms';
import { appTitle, hq, levelLabel, useAuthStore } from '../session';

const { Header, Sider, Content } = Layout;

/** 平台运营端菜单项：带 children 的是二级分组（分组本身不跳转）。 */
interface MenuItem {
  key: string;
  icon?: ReactNode;
  label: string;
  /** 进入该菜单所需权限码（sys_permission.perm_code），数组表示「任一满足」；空表示登录即可见。 */
  perm?: string | readonly string[];
  children?: MenuItem[];
}

/** 新窗口打开的全屏大屏路由（不走账号登录态，凭屏端设备凭据独立取数）；非真实路径，不参与选中匹配。 */
const SCREEN_EXT_KEY = 'ext:/screen';

/**
 * 平台运营端菜单（SYSTEM 门户，docs/05 §4.2）：一级 11 项，按「权限码 + 功能集合」聚出二级分组。
 * 排序按使用频率与运营链路：总览 → 租户/区划 → 机构 → 权限 → 审批 → 平台参数 → 大屏/发布 → 审计。
 */
const MENU: MenuItem[] = [
  // 后端 `/platform/overview` 与 `tenant-metric` 都要求 sys:platform:metric:view（EnsurePlatform），
  // 菜单不带权限码会让无权限账号看得见、点进去才 403。
  { key: '/overview', icon: <DashboardOutlined />, label: '平台总览', perm: 'sys:platform:metric:view' },
  { key: '/tenants', icon: <BankOutlined />, label: '租户管理', perm: 'sys:tenant:view' },
  // 与 docs/07 §7.3 #7 一致：菜单按写权限暴露（列表接口另需 sys:tenant:view，运营角色两者都有）
  { key: '/region', icon: <EnvironmentOutlined />, label: '行政区划', perm: 'sys:region:manage' },
  {
    // 代管租户配置：平台运营跨租户维护（数据范围由 platformAll 决定）
    key: 'g:org',
    icon: <ApartmentOutlined />,
    label: '机构与组织',
    perm: 'sys:org:manage',
    children: [
      { key: '/system/orgs', label: '机构管理' },
      { key: '/system/org-groups', label: '医共体' },
      { key: '/system/org-structure', label: '仓库与科室' },
      { key: '/system/org-space', label: '楼宇与楼层' }
    ]
  },
  { key: '/system/users', icon: <DeploymentUnitOutlined />, label: '用户权限', perm: 'sys:user:manage' },
  { key: '/system/approval', icon: <PartitionOutlined />, label: '审批流配置', perm: 'sys:approval:manage' },
  // 平台级母版与参数：角色/审批流/字典/编号规则模板、指标定义、消息模板、平台参数
  { key: '/platform', icon: <SettingOutlined />, label: '平台基础数据', perm: 'sys:platform:config' },
  { key: '/system/org-config', icon: <ControlOutlined />, label: '机构参数', perm: 'sys:config:manage' },
  {
    // 大屏设备（申请码授权）：全屏展示（新窗口）+ 待确认申请监控 + 已授权设备吊销，按租户查看；含 PC 端申请码确认
    key: 'g:screen',
    icon: <DesktopOutlined />,
    label: '大屏管理',
    perm: 'sys:config:manage',
    children: [
      { key: SCREEN_EXT_KEY, label: '打开大屏' },
      { key: '/system/screen-devices', label: '大屏设备' },
      { key: '/system/screen-scan', label: '申请码确认' }
    ]
  },
  {
    // App 版本管理与发布物分发（发布物落本站点根「apps 目录」，由 system 站点自身服务）
    key: 'g:mobile',
    icon: <MobileOutlined />,
    label: '移动端发布',
    perm: 'sys:config:manage',
    children: [
      { key: '/system/app-versions', label: 'App 版本管理' },
      { key: '/system/app-releases', label: 'App 上传管理' }
    ]
  },
  // 与后端 AuditQuery.EnsureAuditPerm 同口径：两个权限码任一即可（平台门户码等同放行）
  {
    key: '/system/audit',
    icon: <FileSearchOutlined />,
    label: '审计日志',
    perm: ['sys:audit:view', 'sys:platform:audit']
  }
];

// 代管模式下隐藏的平台专属菜单：与路由守卫共用同一份配置（router/routePerms），避免两处漂移

interface PermContext {
  level?: string;
  perms?: readonly string[];
}

/** 递归按权限裁剪：代管态先按 PLATFORM_ONLY_KEYS 剔除，分组节点子项全被裁掉时整组隐藏。 */
function visibleTree(items: readonly MenuItem[], ctx: PermContext, isBlocked: (key: string) => boolean): MenuItem[] {
  return items.flatMap((item) => {
    if (isBlocked(item.key)) return [];
    const allowed = ctx.level === 'admin' || hasPermission(ctx.perms, item.perm, 'any');
    if (!allowed) return [];
    if (!item.children?.length) return [item];
    const children = visibleTree(item.children, ctx, isBlocked);
    return children.length ? [{ ...item, children }] : [];
  });
}

/** antd Menu 只认 key/icon/label/children，交出去前剥掉权限字段。 */
function toMenuItems(items: readonly MenuItem[]): MenuProps['items'] {
  return items.map(({ key, icon, label, children }) =>
    children?.length ? { key, icon, label, children: toMenuItems(children) } : { key, icon, label }
  );
}

interface LeafItem {
  key: string;
  label: string;
  /** 所属分组 key 链，用于自动展开对应的二级菜单。 */
  parents: string[];
}

/** 打平成可跳转的叶子项，并保留父级链（外部入口项不参与路径匹配/标题）。 */
function collectLeaves(items: readonly MenuItem[], parents: string[] = []): LeafItem[] {
  return items.flatMap((item) =>
    item.children?.length
      ? collectLeaves(item.children, [...parents, item.key])
      : item.key.startsWith('ext:')
        ? []
        : [{ key: item.key, label: item.label, parents }]
  );
}

/** 精确命中优先，其次取最长前缀（/system/screen-devices 不被 /system 抢走）。 */
function matchLeaf(leaves: readonly LeafItem[], pathname: string): LeafItem | undefined {
  const exact = leaves.find((leaf) => leaf.key === pathname);
  if (exact) return exact;
  return leaves
    .filter((leaf) => pathname.startsWith(`${leaf.key}/`))
    .sort((a, b) => b.key.length - a.key.length)[0];
}

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useAuthStore((state) => state.profile);
  const clear = useAuthStore((state) => state.clear);
  const patchProfile = useAuthStore((state) => state.patchProfile);
  const enter = useAuthStore((state) => state.enter);
  const endEnter = useAuthStore((state) => state.endEnter);
  const queryClient = useQueryClient();
  // 侧栏受控折叠：底部用简化图标按钮替代默认整条 trigger
  const [collapsed, setCollapsed] = useState(false);
  // 移动端：<768px 侧栏改为抽屉，菜单选中后自动收起
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // 代管倒计时：每秒刷新剩余时间，到期自动退出（令牌本身也会被后端判 401）
  const [now, setNow] = useState(() => Date.now());

  const enterActive = Boolean(enter && enter.expiresAt > now);

  useEffect(() => {
    if (!enter) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [enter]);

  useEffect(() => {
    // 到期自动还原平台令牌，并让所有查询按新令牌重取
    if (enter && enter.expiresAt <= now) {
      endEnter();
      void queryClient.invalidateQueries();
    }
  }, [enter, now, endEnter, queryClient]);

  // 菜单按后端权限码裁剪：管理员不裁剪；无权限快照的会话按 fail-closed 处理（只显示无需权限的菜单）
  const menu = useMemo(() => {
    const blocked = (key: string) => enterActive && PLATFORM_ONLY_KEYS.has(key);
    const tree = visibleTree(MENU, { level: profile?.level, perms: profile?.perms }, blocked);
    return { items: toMenuItems(tree), leaves: collectLeaves(tree) };
  }, [profile?.level, profile?.perms, enterActive]);

  // 子页面高亮所属菜单：先精确匹配，再取最长前缀
  const selected = matchLeaf(menu.leaves, location.pathname)?.key ?? menu.leaves[0]?.key ?? '';

  // 二级菜单展开状态（手风琴）：同一时刻只展开一个分组
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  // 路由变化（含点击二级菜单）时只保留当前页所属分组，其它已展开的分组自动收起
  useEffect(() => {
    setOpenKeys(matchLeaf(menu.leaves, location.pathname)?.parents ?? []);
  }, [location.pathname, menu.leaves]);

  // 手动点开某个分组时收起其它分组；关闭分组时保留仍然展开的那个（再次点击即全部收起）
  const handleOpenChange = (keys: string[]) => {
    setOpenKeys((prev) => {
      const opened = keys.find((key) => !prev.includes(key));
      return opened ? [opened] : keys.slice(-1);
    });
  };

  // 移动端切换菜单后自动收起抽屉
  useEffect(() => {
    if (isMobile) setDrawerOpen(false);
  }, [selected, isMobile]);

  // 侧栏内容（logo + 菜单）：桌面放 Sider，移动端放 Drawer，两端复用同一份
  const siderContent = (
    <>
      <div className="app-logo">
        <img className="app-logo__img" src="/logo.png" alt="logo" />
        {!collapsed && <span title={appTitle}>{truncateByWidth(appTitle)}</span>}
      </div>
      <div className="app-sider__menu">
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selected]}
          // 折叠态由 antd 用悬浮气泡接管展开，传 openKeys 会与之打架
          {...(collapsed && !isMobile ? {} : { openKeys, onOpenChange: handleOpenChange })}
          items={menu.items}
          onClick={({ key }) => (key === SCREEN_EXT_KEY ? window.open('/screen', '_blank') : navigate(key))}
        />
      </div>
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {isMobile ? (
        <Drawer
          placement="left"
          width={232}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          closable={false}
          classNames={{ body: 'app-drawer__body' }}
          styles={{ body: { padding: 0, background: '#001529' } }}
        >
          {siderContent}
        </Drawer>
      ) : (
        <Sider
          className="app-sider"
          theme="dark"
          width={208}
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          trigger={null}
          style={{ position: 'sticky', top: 0, height: '100vh' }}
        >
          {siderContent}
        </Sider>
      )}
      <Layout>
        <Header
          style={{
            background: '#fff',
            paddingInline: isMobile ? 12 : 24,
            height: 56,
            // 不能用 56px 行高：antd ellipsis 单行文本是 inline-block + vertical-align: bottom，
            // 会被 56px 行盒压到最底部，姓名与「退出」就错行了；行高交回 normal 由 flex 居中
            lineHeight: 'normal',
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 16,
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >

          <Space>
            {/* 收起/展开：置顶栏左侧，贴近导航栏；移动端改为抽屉开关 */}
            <Button
              type="text"
              style={{ marginInlineStart: -12 }}
              icon={isMobile ? <MenuOutlined /> : collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => (isMobile ? setDrawerOpen(true) : setCollapsed(!collapsed))}
            />
            {!isMobile && <Typography.Text type="secondary">平台运营端 · 租户与权限</Typography.Text>}
            <Tag color="geekblue">SYSTEM 门户</Tag>
          </Space>
          <Space size={isMobile ? 8 : 16} style={{ flexShrink: isMobile ? 1 : 0, minWidth: 0, whiteSpace: 'nowrap' }}>
            {/* 手机打开：扫码把当前登录态带到手机浏览器（代管只读令牌不发，避免手机进入平台视角） */}
            {!isMobile && !enterActive ? <MobileQrEntry /> : null}
            {profile ? (
              <UserMenu
                hq={hq}
                compact={isMobile}
                subtitle={profile.orgName ?? `机构 ${profile.orgId}`}
                profile={{
                  userId: profile.userId,
                  userCode: profile.userCode,
                  name: profile.name,
                  orgName: profile.orgName,
                  deptName: profile.deptName,
                  mobile: profile.mobile,
                  email: profile.email,
                  avatarUrl: profile.avatarUrl,
                  level: profile.level,
                  levelText: levelLabel(profile.level)
                }}
                onProfileUpdated={(next) =>
                  patchProfile({
                    avatarUrl: next.avatarUrl,
                    deptName: next.deptName,
                    mobile: next.mobile,
                    email: next.email,
                    userCode: next.userCode,
                    orgName: next.orgName ?? profile.orgName
                  })
                }
                onLogout={() => {
                  clear();
                  navigate('/login', { replace: true });
                }}
              />
            ) : (
              <Typography.Text type="secondary">未登录</Typography.Text>
            )}
          </Space>
        </Header>
        {enterActive && enter ? (
          <Alert
            type="warning"
            banner
            showIcon
            style={{ paddingInline: isMobile ? 12 : 24 }}
            message={
              <Space size={12} wrap>
                <span>
                  代管模式：正在查看「{enter.tenantName}」的数据（只读，剩余{' '}
                  {Math.max(0, Math.floor((enter.expiresAt - now) / 60_000))} 分{' '}
                  {Math.max(0, Math.floor(((enter.expiresAt - now) % 60_000) / 1000))} 秒）
                </span>
                <Button
                  size="small"
                  onClick={() => {
                    endEnter();
                    void queryClient.invalidateQueries();
                    navigate('/tenants', { replace: true });
                  }}
                >
                  退出代管
                </Button>
              </Space>
            }
          />
        ) : null}
        <Content style={{ padding: isMobile ? 12 : 24 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
